// ============================================================================
// Edit a setting — the ONE component that writes stock_settings (§15a):
// a clinic's usage rate (decision 5: "a starting estimate that learns, and can
// be edited"), a delivery or fill target, a location's reorder buffer, the
// company starting rate, or which location supplies a clinic.
//
// A setting is history, never an overwrite: "Set by Troy on Oct 2" stays on the
// record, and "Return to automatic" is its own entry. Learning keeps running
// underneath a fixed rate either way.
// ⛔ The draft resets on open and after a successful save.
// ============================================================================

import { useEffect, useState } from 'react';
import { setSetting, type OfficeRow, type SettingKey } from './api';
import { FIELD_CLASS, QtyField, StockWindow, parseQty } from './stock-window';

export type SettingTarget =
  | {
      kind: 'usage';
      facilityId: string;
      supplyId: string;
      title: string;
      unitLabel: string | null;
      learnedMonth: number | null;
      current: number | null;
    }
  | {
      kind: 'target';
      scope: 'facility' | 'office';
      facilityId?: string;
      officeId?: string;
      key: Extract<SettingKey, 'deliver_at_days' | 'fill_to_days' | 'reorder_buffer_days'>;
      title: string;
      current: number;
      isDefault: boolean;
    }
  | {
      kind: 'company_rate';
      supplyId: string;
      title: string;
      unitLabel: string | null;
      current: number | null;
    }
  | {
      kind: 'stock_office';
      facilityId: string;
      title: string;
      current: string | null;
      overridden: boolean;
      offices: OfficeRow[];
    };

const TARGET_WORDS: Record<string, string> = {
  deliver_at_days: 'Deliver when this many days are left',
  fill_to_days: 'Fill up to this many days',
  reorder_buffer_days: 'Reorder this many days before the lead time runs out',
};

export function StockSettingWindow({
  open,
  onClose,
  target,
}: {
  open: boolean;
  onClose: () => void;
  target: SettingTarget | null;
}) {
  const [value, setValue] = useState('');
  const [office, setOffice] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !target) return;
    setUntil('');
    setReason('');
    if (target.kind === 'stock_office') {
      setOffice(target.current ?? '');
      setValue('');
    } else {
      const cur = target.kind === 'usage' ? target.current ?? target.learnedMonth : target.current;
      setValue(cur == null ? '' : String(Math.round(cur * 10000) / 10000));
    }
  }, [open, target]);

  if (!target) return null;

  const n = parseQty(value);
  let canSave = false;
  let title = '';
  let clear: (() => Promise<void>) | undefined;

  if (target.kind === 'usage') {
    title = 'Edit usage rate';
    canSave = n != null && reason.trim().length > 0;
    if (target.current != null) {
      clear = async () => {
        await setSetting({ scope: 'facility', facilityId: target.facilityId, supplyId: target.supplyId, key: 'usage_per_month', clear: true, reason: 'Returned to automatic' });
        onClose();
      };
    }
  } else if (target.kind === 'target') {
    title = TARGET_WORDS[target.key] ?? 'Edit target';
    canSave = n != null && n > 0;
    if (!target.isDefault) {
      clear = async () => {
        await setSetting({ scope: target.scope, facilityId: target.facilityId, officeId: target.officeId, key: target.key, clear: true, reason: 'Back to the standard target' });
        onClose();
      };
    }
  } else if (target.kind === 'company_rate') {
    title = 'Edit the company starting rate';
    canSave = n != null && n > 0 && reason.trim().length > 0;
  } else {
    title = 'Which location supplies this clinic';
    canSave = !!office && office !== target.current;
    if (target.overridden) {
      clear = async () => {
        await setSetting({ scope: 'facility', facilityId: target.facilityId, key: 'stock_office', clear: true, reason: 'Back to the market’s location' });
        onClose();
      };
    }
  }

  async function save() {
    if (!target) return;
    if (target.kind === 'usage') {
      await setSetting({ scope: 'facility', facilityId: target.facilityId, supplyId: target.supplyId, key: 'usage_per_month', value: n, until: until || null, reason: reason.trim() });
    } else if (target.kind === 'target') {
      await setSetting({ scope: target.scope, facilityId: target.facilityId, officeId: target.officeId, key: target.key, value: n, reason: reason.trim() || null });
    } else if (target.kind === 'company_rate') {
      await setSetting({ scope: 'company', supplyId: target.supplyId, key: 'rate_per_chair_night', value: n, reason: reason.trim() });
    } else {
      await setSetting({ scope: 'facility', facilityId: target.facilityId, key: 'stock_office', valueOfficeId: office, reason: reason.trim() || null });
    }
    onClose();
  }

  const clearLabel =
    target.kind === 'usage' ? 'Return to automatic' : target.kind === 'stock_office' ? 'Use the market’s location' : 'Back to standard';

  return (
    <StockWindow
      open={open}
      title={title}
      subtitle={target.title}
      canSave={canSave}
      onCancel={onClose}
      onSave={save}
      secondary={clear ? { label: clearLabel, onClick: clear } : undefined}
    >
      {target.kind === 'usage' && (
        <>
          <p className="text-xs text-tcs-text-muted">
            {target.learnedMonth != null
              ? `Automatic right now: ${Math.round(target.learnedMonth * 100) / 100} ${target.unitLabel === 'tabs' ? 'tabs' : 'gal'} a month. `
              : ''}
            Setting a number overrides it until you return it to automatic. Learning keeps running underneath, and the
            clinic shows “check your setting” if the counts drift more than 30% from it.
          </p>
          <QtyField
            label={target.unitLabel === 'tabs' ? 'Tabs per month' : 'Gallons per month'}
            value={value}
            onChange={setValue}
            step={target.unitLabel === 'tabs' ? 1 : 0.1}
          />
          <label className="block">
            <span className="mb-1 block text-xs text-tcs-text-muted">Keep this setting until (optional)</span>
            <input type="date" className={FIELD_CLASS} value={until} onChange={(e) => setUntil(e.target.value)} />
          </label>
        </>
      )}

      {target.kind === 'target' && (
        <QtyField label="Days" value={value} onChange={setValue} step={1} hint={target.isDefault ? 'This is the standard target.' : undefined} />
      )}

      {target.kind === 'company_rate' && (
        <>
          <p className="text-xs text-tcs-text-muted">
            Used for every clinic that has not been counted yet: rate × chairs × nights cleaned. Once 10 clinics have a
            real observation, their average replaces this automatically.
          </p>
          <QtyField
            label={target.unitLabel === 'tabs' ? 'Tabs per chair per night' : 'Gallons per chair per night'}
            value={value}
            onChange={setValue}
            step={target.unitLabel === 'tabs' ? 0.001 : 0.0001}
          />
        </>
      )}

      {target.kind === 'stock_office' && (
        <label className="block">
          <span className="mb-1 block text-xs text-tcs-text-muted">Supplied from</span>
          <select className={FIELD_CLASS} value={office} onChange={(e) => setOffice(e.target.value)}>
            <option value="">— pick —</option>
            {target.offices
              .filter((o) => o.stock_role === 'stock')
              .map((o) => (
                <option key={o.office_id} value={o.office_id}>
                  {o.office_name}
                </option>
              ))}
          </select>
        </label>
      )}

      <label className="block">
        <span className="mb-1 block text-xs text-tcs-text-muted">
          Reason{target.kind === 'usage' || target.kind === 'company_rate' ? ' (required)' : ' (optional)'}
        </span>
        <input className={FIELD_CLASS} value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
    </StockWindow>
  );
}
